import React from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import * as zod from 'zod'
import { useProductsQuery, useTrackPurchaseFlow } from '@mote/api'
import { useNativeFormMutation } from '@mote/api'
import { TickOutlineIcon } from '@mote/assets/icons'
import { COUNTRIES, __CYPRESS__, __DEV__ } from '@mote/helpers/constants'
import { useCustomForm } from '@mote/helpers/hooks/business'
import {
  Alert,
  Block,
  Button,
  Checkbox,
  CheckboxGroup,
  H2,
  P,
  RadioButton,
  RadioGroup,
  VisuallyHidden
} from '@mote/ui'
import { DefaultLayout } from '../../layouts'
import { Content } from '../../layouts/shared/content'
import {
  DEFAULT_QUOTE_FORM_VALUES,
  MOTE_AMBITION_MODES,
  NATIVE_FORM_ENV_CREDS,
  QUOTE_HANDLING_VALUES,
  QUOTE_USE_MODES
} from './helpers/constants'
import {
  customValidationErrorMap,
  getQuoteFormSchema
} from './helpers/quoteForm.schema'
import { CountryPulldown } from './country.pulldown'
import { ProductData } from './product.data'
import { SmartRegion } from './smart.region'
import {
  Form,
  FormExplanation,
  FormGroup,
  FormInput,
  Link,
  Subtitle,
  Text,
  Title
} from './styles.common'

const quoteUseOptions = [
  {
    value: QUOTE_USE_MODES.WHOLE_SCHOOL_DISTRICT,
    text: 'All educators and students in my district'
  },
  {
    value: QUOTE_USE_MODES.WHOLE_SCHOOL,
    text: 'All educators and students in my school'
  },
  {
    value: QUOTE_USE_MODES.CLASSROOM,
    text: 'One or more classroom groups'
  },
  {
    value: QUOTE_USE_MODES.OTHER,
    text: 'A group of staff and/or students'
  }
]

const quoteHandlingOptions = [
  {
    value: QUOTE_HANDLING_VALUES.FORMAL_QUOTE,
    text: 'I need a formal quote'
  },
  {
    value: QUOTE_HANDLING_VALUES.ORGANISE_A_CALL,
    text: 'I want to organise a call'
  },
  {
    value: QUOTE_HANDLING_VALUES.LICENSE_RENEWAL,
    text: 'I want a quote for renewal of our license'
  }
]

const moteAmbitionOptions = [
  {
    value: MOTE_AMBITION_MODES.SETUP_SCHOOL_30_DAYS,
    text: 'I am interested in setting up in the next 30 days'
  },
  {
    value: MOTE_AMBITION_MODES.SETUP_SCHOOL_60_TO_90_DAYS,
    text: 'I am interested in setting up in the next 60-90 days'
  },
  {
    value: MOTE_AMBITION_MODES.NEXT_SCHOOL_YEAR,
    text: 'I am preparing for budgeting around next school year'
  },
  {
    value: MOTE_AMBITION_MODES.NONE,
    text: 'None of the above'
  }
]

const virtues = [
  'Group discounts for 10+ seats, ideal for teams and departments',
  'Huge bulk savings up to 97% for school- and district-wide adoption',
  'Discounts for multi-year commitments'
]

zod.setErrorMap(customValidationErrorMap)

export const CheckoutSchoolsScreen = () => {
  const history = useHistory()
  const {
    data: rawProducts,
    loading: isRawProductsLoading,
    error: rawProductsError
  } = useProductsQuery()
  const { mutate: trackPurchaseFlow, error: purchaseFlowError } =
    useTrackPurchaseFlow()
  const { mutate: submitNativeForm } = useNativeFormMutation()
  const formRef = React.useRef(null)
  const [isFormSubmitting, setIsFormSubmitting] = React.useState(false)
  const [formSubmissionError, setFormSubmissionError] = React.useState(false)
  const [products, setProducts] = React.useState(new ProductData(null, {}))
  const [studentLicenseQuantity, setStudentLicenseQuantity] = React.useState('')
  const [facultyLicenseQuantity, setFacultyLicenseQuantity] = React.useState('')
  const [quoteUse, setQuoteUse] = React.useState(
    QUOTE_USE_MODES.WHOLE_SCHOOL_DISTRICT
  )
  const [moteAmbition, setMoteAmbition] = React.useState(
    MOTE_AMBITION_MODES.SETUP_SCHOOL_30_DAYS
  )
  const { isOther, isClassroom } = React.useMemo(
    () => ({
      isOther: quoteUse === QUOTE_USE_MODES.OTHER,
      isClassroom: quoteUse === QUOTE_USE_MODES.CLASSROOM
    }),
    [quoteUse]
  )
  const quoteFormSchema = React.useMemo(
    () =>
      getQuoteFormSchema({
        isOther,
        facultyLicenseQuantity,
        studentLicenseQuantity
      }),
    [isOther, facultyLicenseQuantity, studentLicenseQuantity]
  )
  const {
    formState: {
      errors,
      dirtyFields,
      isSubmitted,
      submitCount,
      touchedFields,
      isDirty,
      isSubmitting,
      isSubmitSuccessful,
      isValid
    },
    control,
    handleSubmit,
    setValue,
    getValues,
    resetField,
    trigger
  } = useCustomForm({
    schema: quoteFormSchema,
    defaultValues: DEFAULT_QUOTE_FORM_VALUES
  })
  const subscriptionTermOptions = React.useMemo(() => {
    if (!products || !products.data) return []

    return [
      {
        value: 1,
        text: '1 Year'
      },
      { value: 2, text: `2 Years - ${products.getTermDiscount(2)}% discount` },

      { value: 3, text: `3 Years - ${products.getTermDiscount(3)}% discount` }
    ]
  }, [products])
  const updateTotalQuantity = React.useCallback(
    (value, shouldValidate = true) => {
      setValue('totalQuantity', value, { shouldValidate })

      if (value !== 0 || shouldValidate)
        trigger(['facultyLicenseQuantity', 'studentLicenseQuantity'])
    },
    [setValue]
  )
  const submitForm = React.useCallback(async () => {
    setIsFormSubmitting(true)
    setFormSubmissionError(false)

    try {
      const formValues = getValues()
      const isSchool = !(isOther || isClassroom)
      const productEnum = isSchool
        ? 'schools'
        : products.getProductEnum(formValues.totalQuantity)
      const hasPDHours = formValues.PDHours > 0
      const cappedTotalQuantity =
        isSchool && formValues.totalQuantity < 400
          ? 400
          : formValues.totalQuantity
      const seatsOverride = cappedTotalQuantity !== formValues.totalQuantity

      const data = {
        seatsOverride,
        quoteUse: formValues.quoteUse,
        moteAmbition: formValues.moteAmbition,
        quoteHandling: formValues.quoteHandling.join(','),
        seats: formValues.totalQuantity,
        facultySeats: formValues.facultyLicenseQuantity,
        studentSeats: formValues.studentLicenseQuantity,
        termLength: formValues.termLength,
        seatUnitCost: products.getPerUserCost(cappedTotalQuantity, productEnum),
        seatTotalCost: products.getTotalUserCost(
          cappedTotalQuantity,
          formValues.termLength,
          productEnum
        ),
        hasVolumeDiscount: products.getTermDiscount(formValues.termLength) > 0,
        discountPercentage: products.getTermDiscount(formValues.termLength),
        discountTotal: products.getTotalDiscount(
          cappedTotalQuantity,
          formValues.termLength,
          productEnum
        ),
        hasPDHours,
        totalCost: products.getTotalCost(
          cappedTotalQuantity,
          formValues.termLength,
          formValues.PDHours,
          productEnum
        ),
        institutionName: formValues.institutionName,
        enquirerGivenName: formValues.firstName,
        enquirerFamilyName: formValues.lastName,
        email: formValues.enquirerEmail,
        enquirerPhone: formValues.phoneNumber,
        institutionRegion: formValues.region,
        institutionCountryCode: formValues.countryCode,
        institutionCountryName: COUNTRIES[formValues.countryCode],
        currencySymbol: products.getCurrencySymbol(),
        currencyCode: products.getCurrencyCode(),
        PDHours: formValues.PDHours,
        PDHoursUnitCost: products.getPrettyUnitPriceForPD(),
        PDHoursTotalCost: products.getTotalPDCost(formValues.PDHours)
      }

      const expiryDate = new Date()
      data.quoteExpirationDate = parseInt(
        (expiryDate.getTime() + 30 * 86400 * 1000) / 1000
      )

      if (isOther) data.otherUseDescription = formValues.otherUseDescription
      if (formValues.additionalInfo)
        data.additionalInfo = formValues.additionalInfo

      await trackPurchaseFlow({
        productEnum,
        step: 5,
        stage: 'submitted_quote_form',
        data,
        hasCompleted: true
      })

      await submitNativeForm({
        variables: {
          ...(__DEV__ ? NATIVE_FORM_ENV_CREDS.DEV : NATIVE_FORM_ENV_CREDS.PRD),
          formData: {
            ...data,
            quoteExpirationDate: expiryDate.toISOString(),
            productEnum
          }
        }
      })

      history.push('/checkout/schools/thanks')
    } catch (error) {
      setFormSubmissionError(true)
    }

    setIsFormSubmitting(false)
  }, [isOther, isClassroom, products, getValues])

  React.useEffect(() => {
    trackPurchaseFlow({
      productEnum: 'all',
      step: 4,
      stage: 'Loading quote page',
      hasStart: true
    })
  }, [])

  React.useEffect(() => {
    if (isRawProductsLoading || Object.keys(products.data).length > 0) return

    if (rawProductsError || !rawProducts) {
      trackPurchaseFlow({
        productEnum: 'all',
        step: 4,
        stage: "Couldn't fetch product pricing data for quote page",
        hasFailed: true
      })
      return
    }

    const newProducts = new ProductData(true, rawProducts)
    setProducts(newProducts)
    setValue('countryCode', newProducts.getCountryCode(), {
      shouldValidate: true
    })

    trackPurchaseFlow({
      productEnum: 'all',
      step: 4,
      stage: 'Quote page loaded'
    })
  }, [products, isRawProductsLoading, rawProductsError, rawProducts])

  React.useEffect(() => {
    if (isOther) return

    resetField('otherUseDescription')
  }, [isOther])

  React.useEffect(() => {
    if (facultyLicenseQuantity === '' && studentLicenseQuantity === '') {
      updateTotalQuantity(0, getValues('totalQuantity') !== '')
      return
    }
    if (
      (facultyLicenseQuantity && !Number.isFinite(facultyLicenseQuantity)) ||
      (studentLicenseQuantity && !Number.isFinite(studentLicenseQuantity))
    ) {
      updateTotalQuantity(0)
      return
    }

    updateTotalQuantity(
      studentLicenseQuantity
        ? studentLicenseQuantity + (facultyLicenseQuantity || 0)
        : facultyLicenseQuantity || 0
    )
  }, [
    studentLicenseQuantity,
    facultyLicenseQuantity,
    getValues,
    updateTotalQuantity
  ])

  function updateLicenseQuantity(key, value) {
    if (value === '') {
      trigger(['facultyLicenseQuantity', 'studentLicenseQuantity'])
    }

    if (key === 'studentQuantity') return setStudentLicenseQuantity(value)

    return setFacultyLicenseQuantity(value)
  }

  function updateRadioButtonValue(key, value) {
    const updateValue = key === 'quoteUse' ? setQuoteUse : setMoteAmbition
    setValue(key, value)
    updateValue(value)
  }

  return (
    <DefaultLayout>
      <StyledContent>
        <Block gap={24} spreadX>
          <Block gap={24}>
            <Block gap={8}>
              <H2
                fontSize={32}
                fontWeight={700}
                lineHeight='140%'
                color='#232323'
              >
                Get a Mote quote
              </H2>
              <Subtitle>
                Tell us your needs and we will be in touch soon with a
                customized quote.
              </Subtitle>
            </Block>
            <Block gap={4}>
              {virtues.map(virtue => (
                <Block key={virtue} layout='horizontal' align='center' gap={8}>
                  <TickOutlineIcon />
                  <P
                    fontSize={16}
                    fontWeight={400}
                    lineHeight='140%'
                    color='#232323'
                  >
                    {virtue}
                  </P>
                </Block>
              ))}
            </Block>
          </Block>
          <Block gap={8}>
            <Link
              tag='a'
              href='https://support.mote.com/hc/en-us/articles/4406041881236'
              variant='link'
              text='Learn more about our pricing'
            />
          </Block>
          <Form ref={formRef} onSubmit={handleSubmit(submitForm)}>
            {__CYPRESS__ && (
              <VisuallyHidden id='state'>
                {JSON.stringify({
                  isSubmitted,
                  submitCount,
                  isDirty,
                  isSubmitting,
                  isSubmitSuccessful,
                  isValid,
                  touched: Object.keys(touchedFields),
                  dirty: Object.keys(dirtyFields)
                })}
              </VisuallyHidden>
            )}
            <Block gap={24}>
              <Block gap={8}>
                <Title>Quote details</Title>
                <Subtitle>
                  This information will help us tailor the best pricing for your
                  needs
                </Subtitle>
              </Block>
              <QuotaContainer>
                <FormGroup>
                  <Text>I want a quote for</Text>
                  <RadioGroup
                    value={quoteUse}
                    onChange={value => {
                      updateRadioButtonValue('quoteUse', value)
                    }}
                    gap={16}
                  >
                    {quoteUseOptions.map(option => (
                      <RadioButton
                        key={`quote-use--${option.value}:${option.text}`}
                        value={option.value}
                        label={option.text}
                      />
                    ))}
                  </RadioGroup>
                  {isOther && (
                    <FormInput
                      name='otherUseDescription'
                      htmlType='text'
                      control={control}
                      placeholder='Ooh interesting - tell us more!'
                      required={isOther}
                      disabled={!isOther}
                      styles={{
                        wrapper: {
                          marginLeft: '32px',
                          width: 'calc(100% - 32px)'
                        }
                      }}
                    />
                  )}
                </FormGroup>
              </QuotaContainer>
            </Block>

            <Block gap={32}>
              <FormInput
                name='facultyLicenseQuantity'
                label='Number of teachers and staff'
                htmlType='text'
                placeholder='Enter a quantity'
                control={control}
                onChange={e =>
                  updateLicenseQuantity('facultyQuantity', e.target.value)
                }
                required
              />

              <FormGroup>
                <FormInput
                  label='Number of STUDENTS'
                  name='studentLicenseQuantity'
                  htmlType='text'
                  placeholder='Enter a quantity'
                  control={control}
                  onChange={e =>
                    updateLicenseQuantity('studentQuantity', e.target.value)
                  }
                  required
                />
                {errors.totalQuantity && (
                  <FormExplanation isError>
                    {errors.totalQuantity.message}
                  </FormExplanation>
                )}
              </FormGroup>

              <FormInput
                label='Subscription Term Length'
                type='select'
                name='termLength'
                control={control}
                options={subscriptionTermOptions}
                placeholder='Select a term length'
                required
              />

              <FormInput
                label={`Online PD Sessions - ${
                  products.getPrettyUnitPriceForPD().split('.')[0]
                } Each`}
                name='PDHours'
                htmlType='number'
                placeholder='Enter a quantity'
                control={control}
                required
              />
            </Block>

            <Block gap={32}>
              <Title>Personal details</Title>

              <FormInput
                label='First Name'
                name='firstName'
                htmlType='text'
                placeholder='Enter first name'
                control={control}
                required
              />

              <FormInput
                label='Last Name'
                name='lastName'
                htmlType='text'
                placeholder='Enter last name'
                control={control}
                required
              />

              <FormInput
                label='Organization Name'
                name='institutionName'
                htmlType='text'
                placeholder='Enter organization name'
                control={control}
                required
              />

              <FormInput
                label='Email address'
                name='enquirerEmail'
                htmlType='text'
                placeholder='Enter email'
                control={control}
                required
              />

              <FormInput
                label='Phone Number'
                name='phoneNumber'
                htmlType='text'
                placeholder='Enter phone number'
                control={control}
                parseValue={false}
                required
              />

              <CountryPulldown
                label='Country'
                name='countryCode'
                control={control}
                onChange={() => {
                  resetField('region')
                }}
                required
              />

              <SmartRegion
                name='region'
                control={control}
                region={getValues('region')}
                disabled={getValues('countryCode') === ''}
                countryCode={getValues('countryCode')}
                required
              />

              <FormGroup>
                <Block gap={8}>
                  <Text required={false}>Additional Information</Text>
                  <Subtitle>
                    Please add any information you think may help us when
                    generating your quote
                  </Subtitle>
                </Block>
                <FormInput
                  name='additionalInfo'
                  placeholder='E.g. who/what/why are you looking to power your school with Mote?'
                  type='textarea'
                  control={control}
                  rows={10}
                  required={false}
                />
              </FormGroup>

              <Block gap={24}>
                <Title>Help us understand your Mote ambition</Title>
                <RadioGroup
                  value={moteAmbition}
                  onChange={value => {
                    updateRadioButtonValue('moteAmbition', value)
                  }}
                >
                  {moteAmbitionOptions.map(option => (
                    <RadioButton
                      key={`mote-ambition--${option.value}:${option.text}`}
                      value={option.value}
                      label={option.text}
                    />
                  ))}
                </RadioGroup>
              </Block>

              <QuotaContainer>
                <FormGroup>
                  <Block gap={24}>
                    <Text>Check all that apply</Text>
                    <CheckboxGroup
                      onChange={values => {
                        setValue('quoteHandling', values, {
                          shouldValidate: true
                        })
                      }}
                      gap={16}
                    >
                      {quoteHandlingOptions.map(option => (
                        <Checkbox
                          key={`quote-handling--${option.value}:${option.text}`}
                          value={option.value}
                          label={option.text}
                        />
                      ))}
                    </CheckboxGroup>
                  </Block>
                  {errors.quoteHandling && (
                    <FormExplanation isError>
                      {errors.quoteHandling.message}
                    </FormExplanation>
                  )}
                </FormGroup>
              </QuotaContainer>

              <FormGroup style={{ gap: '20px' }}>
                {(purchaseFlowError || formSubmissionError) && (
                  <Alert
                    type='error'
                    message='Something went wrong while creating your quote. Please refresh the page and try again!'
                  />
                )}
                <Button
                  size='lg'
                  htmlType='submit'
                  text='Create my quote'
                  loadingText='Creating...'
                  loading={isFormSubmitting}
                  autoWidth
                />
                <Block gap={12}>
                  <P
                    fontSize={15}
                    fontWeight={400}
                    lineHeight='140%'
                    color='#232323'
                  >
                    You will receive email confirmation to{' '}
                    {getValues('enquirerEmail')}. We’ll be in touch shortly to
                    discuss your quote.
                  </P>
                </Block>
              </FormGroup>
            </Block>
          </Form>
        </Block>
      </StyledContent>
    </DefaultLayout>
  )
}

const StyledContent = styled(Content)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 760px;

  @media (max-width: 1279px) {
    max-width: 820px;
  }

  @media (max-width: 991px) {
    max-width: 780px;
  }

  @media (max-width: 639px) {
    max-width: 740px;
  }
`

const QuotaContainer = styled(Block)`
  padding: 28px;
  background: #f9f9fb;
  border-radius: 8px;
`
